import React, { useCallback, useEffect, useMemo } from 'react';
import { injectIntl } from 'react-intl-next';
import Tooltip from '@atlaskit/tooltip';
import Button, { type Appearance } from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import ArrowsDiagonalUpRightDownLeftIcon from '@atlaskit/icon-lab/core/arrows-diagonal-up-right-down-left';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import messages from './messages';
import { type Props, type NonAnalyticsProps } from './types';
import { Wrapper, PaddedIcon, SpaceOrPageTitleWrapper } from './styled';
import { PageIcon, LiveDocIcon, SpaceIcon } from '../common/styled';
import { ContextualAnalyticsData, DROPDOWN, fireUIAnalytics } from '@atlassian/analytics-bridge';
import { useThemeContext } from '../../ui/theme';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline, Stack } from '@atlaskit/primitives/compiled';

const MoreMenuComponent = injectIntl(
	({
		triggerShowConnectSpaceDialog,
		triggerFetchConnectedSpacePageTitle,
		isSpaceConnected,
		spaceOrPageTitle,
		spaceOrPageLink,
		isConnectedToPage,
		isConnectedToLiveDoc,
		spaceIcon,
		isProjectMember,
		intl: { formatMessage },
	}: Props) => {
		const handleUpdateSpaceOrPageClick = useCallback(
			(analyticsEvent: UIAnalyticsEvent) => {
				triggerShowConnectSpaceDialog();
				fireUIAnalytics(analyticsEvent, 'updateSpaceOrPageButton', {
					isConnectedToPage,
				});
			},
			[isConnectedToPage, triggerShowConnectSpaceDialog],
		);

		const handleNavigateToConnectedSpaceOrPageClick = (analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'navigateToConnectedSpaceOrPageButton', {
				isConnectedToPage,
			});
		};

		const getConnectedContentTooltipMessage = () => {
			if (isConnectedToPage) {
				if (isConnectedToLiveDoc && fg('confluence_live_pages_enabled_in_jira')) {
					return messages.goToConnectedLiveDocTooltip;
				}
				return messages.goToConnectedPageTooltip;
			}
			return messages.goToConnectedSpaceTooltip;
		};

		useEffect(() => {
			if (isSpaceConnected) {
				triggerFetchConnectedSpacePageTitle();
			}
		}, [triggerFetchConnectedSpacePageTitle, isSpaceConnected]);

		const {
			maxWidth,
			appearance,
		}: {
			maxWidth: string;
			appearance: Appearance;
		} = { maxWidth: '208px', appearance: 'default' };

		const connectionIcon = useMemo((): React.ReactChild | undefined => {
			if (isConnectedToPage) {
				const PageTypeIcon =
					isConnectedToLiveDoc && fg('confluence_live_pages_enabled_in_jira')
						? LiveDocIcon
						: PageIcon;

				return <PageTypeIcon />;
			}
			if (spaceIcon) {
				return (
					<PaddedIcon>
						<SpaceIcon iconPath={spaceIcon} />
					</PaddedIcon>
				);
			}
			return undefined;
		}, [isConnectedToPage, isConnectedToLiveDoc, spaceIcon]);
		const theme = useThemeContext();

		return (
			<Wrapper>
				{spaceOrPageTitle && (
					<Tooltip content={formatMessage(getConnectedContentTooltipMessage())}>
						<Button
							appearance="subtle-link"
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							style={{ maxWidth: maxWidth }}
							href={spaceOrPageLink || undefined}
							onClick={(_, analyticsEvent) =>
								handleNavigateToConnectedSpaceOrPageClick(analyticsEvent)
							}
							target="_blank"
							spacing={isProjectMember ? 'default' : 'none'}
						>
							<Stack alignInline="center">
								<Inline alignBlock="center" space="space.075" grow="fill">
									{connectionIcon}
									<SpaceOrPageTitleWrapper theme={theme}>
										{spaceOrPageTitle}
									</SpaceOrPageTitleWrapper>
								</Inline>
							</Stack>
						</Button>
					</Tooltip>
				)}
				{isProjectMember && (
					<Tooltip
						content={
							spaceOrPageTitle
								? formatMessage(messages.updateConnectedSpaceOrPageTooltip)
								: formatMessage(messages.connectedSpaceOrPageTooltip)
						}
					>
						<IconButton
							label={formatMessage(messages.label)}
							appearance={appearance}
							onClick={(_, analyticsEvent) => handleUpdateSpaceOrPageClick(analyticsEvent)}
							icon={(iconProps) => <ArrowsDiagonalUpRightDownLeftIcon {...iconProps} />}
							data-test-id="confluence-project-pages.post-expand.connect-or-update-space-button"
						/>
					</Tooltip>
				)}
			</Wrapper>
		);
	},
);

export default (props: NonAnalyticsProps) => (
	<ContextualAnalyticsData sourceName="moreMenu" sourceType={DROPDOWN}>
		<MoreMenuComponent {...props} />
	</ContextualAnalyticsData>
);
